// src/lib/env/env-guard.ts
import { z } from 'zod';

/**
 * ENV Guard — строгая валидация окружения.
 *
 * Ключевые идеи:
 * - На клиенте доступен только PUBLIC-слой (VITE_*).
 * - На сервере доступны оба: PUBLIC и SERVER.
 * - Никаких console.*, все ошибки — через throw с понятным сообщением.
 * - Кэширование результатов, чтобы не парсить по сто раз.
 *
 * Совместимость:
 * - ensureSupabaseClientConfig() — заменяет старый хелпер, возвращает { url, projectId }.
 * - getPublicEnv() — для client (Vite).
 * - getServerEnv() — для серверных рантаймов (Vercel API / Edge / Supabase Functions).
 */

/* ---------------------------------- Utils ---------------------------------- */

const isBrowser = (): boolean =>
  typeof window !== 'undefined' && typeof document !== 'undefined';

const coerceUrl = z
  .string()
  .min(1, 'required')
  .transform((s, ctx) => {
    try {
      // нормализуем до абсолютного URL (проверка валидности)
      const u = new URL(s);
      return u.toString().replace(/\/+$/, ''); // без конечного слеша
    } catch {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'invalid_url' });
      return z.NEVER;
    }
  });

/* ----------------------------- Public (client) ----------------------------- */
/**
 * Только то, что можно отдавать в браузер.
 * В Vite это import.meta.env.VITE_*
 */
const PublicEnvSchema = z.object({
  // Режим (Vite предоставляет MODE/DEV/PROD, но оставим NODE_ENV для унификации)
  NODE_ENV: z.enum(['development', 'test', 'production']).optional(),

  // Критично для Supabase client в браузере:
  VITE_SUPABASE_URL: coerceUrl,
  VITE_SUPABASE_PROJECT_ID: z.string().min(5).optional(),

  // Опционально могут присутствовать публичные URL витрин (если заведёшь):
  VITE_APP_PUBLIC_URL: coerceUrl.optional(),
  VITE_ADMIN_PUBLIC_URL: coerceUrl.optional(),
});

type PublicEnv = z.infer<typeof PublicEnvSchema>;

/* ----------------------------- Server (private) ---------------------------- */
/**
 * Серверные переменные. Не импортируй этот модуль в клиентский бандл.
 * Здесь лежат домены, редиректы, флаги безопасности и т.п.
 */
const ServerEnvSchema = z.object({
  NODE_ENV: z.enum(['development', 'test', 'production']),

  // Источники правды для адаптера доменов/роутов:
  APP_PUBLIC_URL: coerceUrl,
  ADMIN_PUBLIC_URL: coerceUrl,
  API_BASE_URL: coerceUrl,
  HEALTH_URL: coerceUrl,

  // Дополнительно (может быть пустым):
  ASSETS_CDN_URL: coerceUrl.optional(),
  MAGIC_LINK_DOMAIN: z.string().min(3).optional(),

  // OAuth редиректы (могут отсутствовать, если провайдеры не включены)
  OAUTH_GOOGLE_REDIRECT: coerceUrl.optional(),
  OAUTH_APPLE_REDIRECT: coerceUrl.optional(),

  // Supabase server-side (например для Edge/Functions). Не экспортируются в клиент.
  SUPABASE_URL: coerceUrl,
  SUPABASE_ANON_KEY: z.string().min(10),
  SUPABASE_SERVICE_ROLE_KEY: z.string().min(10),

  // Безопасность CORS (включать origin Supabase UI — только осознанно)
  ALLOW_SUPABASE_ORIGIN: z.enum(['true', 'false']).default('false'),

  // Bridge webhook интеграция
  BRIDGE_WEBHOOK_SECRET: z.string().min(1),
  BRIDGE_WEBHOOK_IDEMPOTENCY_TTL_SEC: z.string().regex(/^\d+$/).transform(Number).default('86400'),

  // Atlas signals интеграция
  ATLAS_SIGNAL_INTERNAL_TOKEN: z.string().min(1),
});

type ServerEnv = z.infer<typeof ServerEnvSchema>;

/* ------------------------------- Cache layers ------------------------------ */

let _publicCache: PublicEnv | null = null;
let _serverCache: ServerEnv | null = null;

function deriveProjectIdFromUrl(value?: string): string | undefined {
  if (!value) return undefined;
  try {
    const host = new URL(value).hostname;
    const match = host.match(/^([a-z0-9-]+)\.supabase\.(co|in)$/i);
    return match?.[1];
  } catch {
    return undefined;
  }
}

/* ------------------------------- Public API -------------------------------- */

/**
 * Возвращает валидные PUBLIC env (для браузера/клиента).
 * Источник — import.meta.env (если есть) или process.env (fallback).
 */
export function getPublicEnv(): PublicEnv {
  if (_publicCache) return _publicCache;

  // В Vite переменные лежат в import.meta.env, в ноде — в process.env
  const bag: Record<string, unknown> = isBrowser()
    ? // @ts-expect-error — в рантайме Vite существует
      (typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env : {})
    : process.env;

  const parsed = PublicEnvSchema.safeParse(bag);
  if (!parsed.success) {
    const msg =
      '[env/public] invalid config: ' +
      parsed.error.issues.map((i) => `${i.path.join('.')}:${i.message}`).join('; ');
    throw new Error(msg);
  }
  const resolvedProjectId =
    parsed.data.VITE_SUPABASE_PROJECT_ID ||
    deriveProjectIdFromUrl(parsed.data.VITE_SUPABASE_URL);
  if (!resolvedProjectId) {
    throw new Error('[env/public] missing VITE_SUPABASE_PROJECT_ID (unable to derive from VITE_SUPABASE_URL)');
  }
  _publicCache = { ...parsed.data, VITE_SUPABASE_PROJECT_ID: resolvedProjectId };
  return _publicCache;
}

/**
 * Возвращает валидные SERVER env (только для серверных рантаймов).
 * Если вызвать в браузере — бросит ошибку.
 */
export function getServerEnv(): ServerEnv {
  if (isBrowser()) {
    throw new Error('[env/server] attempted to read server env in browser');
  }
  if (_serverCache) return _serverCache;

  const parsed = ServerEnvSchema.safeParse(process.env);
  if (!parsed.success) {
    const msg =
      '[env/server] invalid config: ' +
      parsed.error.issues.map((i) => `${i.path.join('.')}:${i.message}`).join('; ');
    throw new Error(msg);
  }
  _serverCache = parsed.data;
  return _serverCache;
}

/**
 * Удобный хелпер для клиентского Supabase:
 * — возвращает URL и projectId;
 * — бросает, если что-то не так.
 */
export function ensureSupabaseClientConfig(): {
  url: string;
  projectId: string;
} {
  const pub = getPublicEnv();
  return {
    url: pub.VITE_SUPABASE_URL,
    projectId: pub.VITE_SUPABASE_PROJECT_ID,
  };
}

/**
 * Флаги режима (унифицировано).
 * На клиенте — завязаны на PublicEnv.NODE_ENV (если задан).
 */
export function isProd(): boolean {
  try {
    const env = isBrowser() ? getPublicEnv().NODE_ENV : getServerEnv().NODE_ENV;
    return (env ?? process.env.NODE_ENV) === 'production';
  } catch {
    // Если ранняя стадия и схемы ещё не заполнены — fallback к NODE_ENV
    return process.env.NODE_ENV === 'production';
  }
}

/* --------------------------- Safe access shortcuts -------------------------- */

/**
 * Общий геттер с указанием ключа из SERVER env.
 * Упрощает миграции (когда хочешь локально получить конкретный ключ).
 * Не используй в клиенте.
 */
export function requireServer<K extends keyof ServerEnv>(key: K): ServerEnv[K] {
  const env = getServerEnv();
  return env[key];
}

/**
 * Общий геттер с указанием ключа из PUBLIC env.
 * Разрешён в браузере.
 */
export function requirePublic<K extends keyof PublicEnv>(key: K): PublicEnv[K] {
  const env = getPublicEnv();
  return env[key];
}

/* ---------------------------------- Debug helpers ---------------------------------- */

export interface EnvGuardError {
  key: string;
  value: string | undefined;
  message: string;
  severity: 'error' | 'warning';
}

/**
 * Get environment information for debugging
 */
export function getEnvInfo() {
  const publicEnv = getPublicEnv();

  return {
    env: publicEnv.VITE_RUNTIME_ENV || 'unknown',
    supabaseUrl: publicEnv.VITE_SUPABASE_URL,
    supabaseProjectId: publicEnv.VITE_SUPABASE_PROJECT_ID,
    buildSha: publicEnv.VITE_BUILD_SHA || 'unknown',
    timestamp: new Date().toISOString()
  };
}

/**
 * Validate environment configuration for a domain
 */
export function validateEnvForDomain(domain: string): { isValid: boolean; errors: EnvGuardError[] } {
  const errors: EnvGuardError[] = [];
  const publicEnv = getPublicEnv();

  // Check required public env vars
  if (!publicEnv.VITE_SUPABASE_URL) {
    errors.push({
      key: 'VITE_SUPABASE_URL',
      value: publicEnv.VITE_SUPABASE_URL,
      message: 'Missing Supabase URL',
      severity: 'error'
    });
  }

  if (!publicEnv.VITE_SUPABASE_PROJECT_ID) {
    errors.push({
      key: 'VITE_SUPABASE_PROJECT_ID',
      value: publicEnv.VITE_SUPABASE_PROJECT_ID,
      message: 'Missing Supabase Project ID',
      severity: 'error'
    });
  }

  if (!publicEnv.VITE_RUNTIME_ENV) {
    errors.push({
      key: 'VITE_RUNTIME_ENV',
      value: publicEnv.VITE_RUNTIME_ENV,
      message: 'Missing runtime environment',
      severity: 'warning'
    });
  }

  return {
    isValid: errors.filter(e => e.severity === 'error').length === 0,
    errors
  };
}

/**
 * Validate environment at build time - throws if invalid
 */
export function validateEnvAtBuildTime(): void {
  try {
    const publicEnv = getPublicEnv();

    // Critical build-time validations
    if (!publicEnv.VITE_SUPABASE_URL) {
      throw new Error('[env/build] VITE_SUPABASE_URL is required at build time');
    }

    if (!publicEnv.VITE_SUPABASE_PROJECT_ID) {
      throw new Error('[env/build] VITE_SUPABASE_PROJECT_ID is required at build time');
    }

    // Validate URL format
    try {
      new URL(publicEnv.VITE_SUPABASE_URL);
    } catch {
      throw new Error('[env/build] VITE_SUPABASE_URL must be a valid URL');
    }

  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Build-time environment validation failed: ${error.message}`);
    }
    throw new Error('Build-time environment validation failed: unknown error');
  }
}

/**
 * Guard environment for domain - throws if validation fails
 */
export function guardEnvForDomain(): void {
  try {
    // Use current domain or default
    const domain = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    const result = validateEnvForDomain(domain);

    // Throw if there are critical errors
    const criticalErrors = result.errors.filter(e => e.severity === 'error');
    if (criticalErrors.length > 0) {
      const errorMessages = criticalErrors.map(e => `${e.key}: ${e.message}`).join('; ');
      throw new Error(`[env/guard] Environment validation failed for domain ${domain}: ${errorMessages}`);
    }

  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('[env/guard] Environment guard failed: unknown error');
  }
}

/* ---------------------------------- Notes ---------------------------------- *
 * - Не импортируй этот файл в client-only компоненты, если тебе нужен getServerEnv().
 * - Для доменных URL/алиасов используем Domain Adapter (packages/config/*),
 *   который опирается на эти Server env. Этот модуль лишь валидирует значения.
 * - Ошибки конфигурации должны проявляться фейлом рантайма сразу при старте,
 *   а не «где-то потом в консоли».
 * --------------------------------------------------------------------------- */
